import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  fetchRecruiterStats,
  clearError,
} from "../redux/slices/RecruitersSlice"; // Adjust the import path as needed

const StatsCard = ({
  title,
  value,
  valueColor = "#8b5cf6",
  loading = false,
  growthText = "",
  lastUpdate = "",
  lastUpdateValue = "",
}) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5, md: 3, xl: 3 },
      borderRadius: 2,
      backgroundColor: "#fff",
      border: "1px solid #e5e7eb",
      width: { xs: "100%", sm: "100%", md: "100%", xl: "457px" },
      minWidth: { xs: "315px", sm: "320px", md: "380px" },
      minHeight: { xs: "160px", sm: "170px", md: "180px", xl: "auto" },
      display: "flex",
      flexDirection: "column",
      mb: "-10%",
    }}
  >
    {/* Header Section */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: { xs: 1.5, sm: 1.5, md: 2, xl: 2 },
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0.5, sm: 0 },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: { xs: "15px", sm: "16px", md: "17px", xl: "17px" },
          fontWeight: 500,
          color: "#374151",
          lineHeight: 1.2,
          fontFamily: "Outfit",
        }}
      >
        {title}
      </Typography>
    </Box>

    {/* Value Section */}
    <Box sx={{ mb: { xs: 1.5, sm: 1.5, md: 2, xl: 2 } }}>
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", height: "2.5rem" }}>
          <CircularProgress size={24} sx={{ color: valueColor }} />
        </Box>
      ) : (
        <Typography
          variant="h1"
          sx={{
            color: valueColor,
            fontSize: { xs: "2rem", sm: "2.2rem", md: "2.4rem", xl: "2.5rem" },
            lineHeight: 1,
            textAlign: "left",
            fontFamily: "Outfit",
            fontWeight: 700,
            mb: { xs: "-2%", sm: "0", md: "0", xl: "-5%" },
          }}
        >
          {value}
        </Typography>
      )}
    </Box>

    {/* Growth/Additional Info Section */}
    {growthText && (
      <Box sx={{ mt: "auto" }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: "12px", sm: "13px", md: "14px" },
            color: "#6b7280",
            fontFamily: "Outfit",
          }}
        >
          {growthText}
        </Typography>
      </Box>
    )}

    {/* Last Update Section */}
    {lastUpdate && lastUpdateValue && (
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: "12px", sm: "13px", md: "14px" },
            color: "#6b7280",
            fontFamily: "Outfit",
          }}
        >
          {lastUpdate}: {lastUpdateValue}
        </Typography>
      </Box>
    )}
  </Paper>
);

export default function RecruitersStasGrid({ sidebarOpen = true }) {
  const dispatch = useDispatch();

  // Get data from Redux store
  const { statsData, loading, error } = useSelector(
    (state) => state.resumeStats
  );

  // Fetch data when component mounts
  useEffect(() => {
    dispatch(fetchRecruiterStats());
  }, [dispatch]);

  // Handle error clearing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000); // Clear error after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarOpen ? 200 : 100;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4, xl: 4 },
        backgroundColor: "#fff",
        ml: {
          xs: "0px",
          sm: "0px",
          md: `${sidebarWidth}px`,
          xl: `${sidebarWidth}px`,
        },
        transition: "margin-left 0.3s ease",
      }}
    >
      <Box sx={{ mx: "auto" }}>
        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => dispatch(clearError())}
          >
            {error}
          </Alert>
        )}

        <Grid
          container
          spacing={{ xs: 2, sm: 2.5, md: 3, xl: 3 }}
          sx={{ display: "flex", flexWrap: "wrap" }}
        >
          {statsData.map((stat, index) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={4}
              key={index}
              sx={{ display: "flex" }}
            >
              <StatsCard {...stat} loading={loading} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
